import type { PermissionCode } from '@/auth/types';
import { ZONE_PATHS, type Zone } from '@/auth/zone-home';

/**
 * MENU = VERI. Her bolgenin gezinme agaci burada, donmus (frozen) bir dizi
 * olarak durur; SidebarNav onu yalniz cizer.
 *
 * `labelKey` — `label` DEGIL. Modul seviyesinde t() cagirmak metni IMPORT
 * ANINDA dondurur ve dil degistiginde bayat birakir. Alan adinin bunu okurken
 * belli etmesi gerekiyor: `label: t('...')` yazan biri hatayi fark etmez,
 * `labelKey: t('...')` yazan biri eder. (Ayni kural src/design/theme.ts
 * icindeki THEME_OPTIONS icin de gecerli.)
 *
 * `id` bir ANAHTAR DEGIL. Menu ogesi baska yerlerde kimlikle eslesiyor; ceviri
 * dosyasindan gecirilmesi gereken tek alan `labelKey`.
 *
 * ============================== GRUPLAMA ====================================
 * Gruplama oge BASINA bir alanla (`groupKey`) tasiniyor, ic ice bir yapiyla
 * DEGIL. Sebep: bu dizi ayni zamanda ROTA KAYNAGI (router.tsx menuden rota
 * uretiyor) ve uc ayri test onu duz liste olarak okuyor. Ic ice yapi, salt
 * GORSEL bir gruplama ugruna o tuketicilerin hepsini duzlestirmeye zorlardi.
 *
 * Bedeli: ayni grubun ogeleri BITISIK olmazsa ayni baslik iki kez cizilir.
 * Bu sessiz bir hata olurdu, bu yuzden testle baglandi (SidebarNav.test.tsx —
 * "her grup BITISIK"). Grup basligi olmayan oge (Kurlar) listenin basinda
 * bassiz durur.
 * ========================================================================== */
export type MenuItem = Readonly<{
  /** Kararli kimlik. CEVRILMEZ, gorunmez, degistirilmez. */
  id: string;
  /** react-router yolu (taban yol olmadan). */
  to: string;
  /** CEVIRI ANAHTARI — metin degil. */
  labelKey: string;
  /**
   * Grup basliginin CEVIRI ANAHTARI. Yoksa oge bassiz cizilir (acilis ogesi).
   * Ayni grubun ogeleri dizide BITISIK olmali.
   */
  groupKey?: string;
  /**
   * Ogenin NEREDE cizilecegi. Varsayilan kenar cubugu.
   *
   * `'topbar'` = rota bu listeden URETILIR ama kenar cubugunda GORUNMEZ; kabuk
   * onu sag ust kosede kendi bicimiyle cizer (profil avatari). Oge listeden
   * SILINMEDI cunku rotalar buradan uretiliyor (router.tsx) — silinseydi
   * /profil rotasi da yok olurdu. "Menude olan her seyin rotasi, rotasi olan
   * her seyin menude karsiligi var" degismezi boylece korunuyor; degisen tek
   * sey CIZIM YERI.
   */
  placement?: 'topbar';
  /**
   * Gerekli buro yetkisi. Liste verilirse "bunlardan BIRI" yeterli. Yoksa oge menude gorunmez.
   */
  permission?: PermissionCode | readonly PermissionCode[];
  /**
   * Yalniz bir organizasyonun uyesine gorunur. Org'suz kullanici yetki kontrolunden gecer
   * (grant sisteminin disinda), ama organizasyon raporlari sunucuda org baglami ister.
   */
  requiresOrganization?: boolean;
  /** Yalniz buro YONETICISINE gorunur (ortuk yetkiler). */
  adminOnly?: boolean;
}>;

const freeze = (items: MenuItem[]): readonly MenuItem[] =>
  Object.freeze(items.map((item) => Object.freeze(item)));

/**
 * Musteri bolgesi.
 *
 * Yetki KODLARI burada calismaz (hicbir ogede `permission` yok) — grant tablosu buro ici
 * yetkileri tasir. Ama 2026-08-30'dan beri UYE TIPI ekseni burada da calisiyor: musteri
 * organizasyonunun yoneticisi ekip/isletme/gecmis ekranlarini gorur, personel gormez.
 * `adminOnly` bolgeden bagimsiz bir kapi (SidebarNav -> isOrgAdmin), yani bu ogelerin
 * eklenmesi navigasyon altyapisinda hicbir degisiklik gerektirmedi.
 */
const customerMenu = freeze([
  // Kurlar ana sayfa: mobilde de acilis ekrani bu ve talep olusturma akisi buradan basliyor
  // (kur karti -> talep). Menu sirasi mobil sekme sirasini izler.
  { id: 'customer.rates', to: ZONE_PATHS.customer, labelKey: 'nav.rates' },
  {
    id: 'customer.requests',
    to: `${ZONE_PATHS.customer}/talepler`,
    labelKey: 'nav.myRequests',
    groupKey: 'nav.groupOperations',
  },
  // "Gelen teklifler" (customer.offers) 2026-10-02'de KALDIRILDI: teklifler talebin kendi
  // sayfasinda canli. Eski adres (/musteri/teklifler) Taleplerim'e yonlendiriyor
  // (features/tasks/screens.ts, taskExtraRoutes).
  {
    id: 'customer.accounts',
    to: `${ZONE_PATHS.customer}/hesaplar`,
    labelKey: 'nav.accounts',
    groupKey: 'nav.groupOperations',
  },
  // Kisisel panel: HERKESE acik (adminOnly yok). Kapsami cagiranin KENDI verisi — yonetici de
  // burada kendi rakamlarini gorur, org geneli icin ayri bolum var.
  {
    id: 'customer.dashboard',
    to: `${ZONE_PATHS.customer}/panel`,
    labelKey: 'nav.dashboard',
    groupKey: 'nav.groupInsights',
  },
  {
    id: 'customer.organization',
    to: `${ZONE_PATHS.customer}/isletme`,
    labelKey: 'nav.organization',
    groupKey: 'nav.groupManagement',
    adminOnly: true,
  },
  {
    id: 'customer.members',
    to: `${ZONE_PATHS.customer}/ekip`,
    labelKey: 'nav.team',
    groupKey: 'nav.groupManagement',
    adminOnly: true,
  },
  {
    id: 'customer.auditLog',
    to: `${ZONE_PATHS.customer}/gecmis`,
    labelKey: 'nav.auditLog',
    groupKey: 'nav.groupManagement',
    adminOnly: true,
  },
  {
    id: 'customer.profile',
    to: `${ZONE_PATHS.customer}/profil`,
    labelKey: 'nav.profile',
    placement: 'topbar',
  },
]);

/** Buro bolgesi. Yetki kodlari sunucudaki grant tablosuyla BIREBIR ayni adlar. */
const exchangeMenu = freeze([
  // Buro icin de acilis Kurlar; kartlar salt okunur (teklif akisi Piyasa'dan yurur).
  { id: 'exchange.rates', to: ZONE_PATHS.exchange, labelKey: 'nav.rates' },
  {
    id: 'exchange.requests',
    to: `${ZONE_PATHS.exchange}/piyasa`,
    labelKey: 'nav.quoteRequests',
    groupKey: 'nav.groupOperations',
    permission: 'viewQuoteRequests',
  },
  {
    id: 'exchange.offers',
    to: `${ZONE_PATHS.exchange}/teklifler`,
    labelKey: 'nav.myOffers',
    groupKey: 'nav.groupOperations',
    // Teklif veren, guncelleyen ve kazanilan islemde "Odeme alindi" diyen personelin ortak
    // ekrani (2026-09-23). Yalniz createOffer'a baglanmasi, yalniz completeTask yetkisi olan
    // personeli odeme onayindan disarida birakiyordu.
    permission: ['createOffer', 'editOffer', 'completeTask'],
  },
  {
    id: 'exchange.accounts',
    to: `${ZONE_PATHS.exchange}/hesaplar`,
    labelKey: 'nav.accounts',
    groupKey: 'nav.groupOperations',
    permission: 'viewAccounts',
  },
  {
    id: 'exchange.dashboard',
    to: `${ZONE_PATHS.exchange}/panel`,
    labelKey: 'nav.dashboard',
    groupKey: 'nav.groupInsights',
    permission: 'viewReport',
  },
  {
    id: 'exchange.reports',
    to: `${ZONE_PATHS.exchange}/raporlar`,
    labelKey: 'nav.reports',
    groupKey: 'nav.groupInsights',
    permission: 'viewReport',
    requiresOrganization: true,
  },
  {
    id: 'exchange.organization',
    to: `${ZONE_PATHS.exchange}/isletme`,
    labelKey: 'nav.organization',
    groupKey: 'nav.groupManagement',
    adminOnly: true,
  },
  {
    id: 'exchange.members',
    to: `${ZONE_PATHS.exchange}/ekip`,
    labelKey: 'nav.team',
    groupKey: 'nav.groupManagement',
    adminOnly: true,
  },
  {
    id: 'exchange.auditLog',
    to: `${ZONE_PATHS.exchange}/gecmis`,
    labelKey: 'nav.auditLog',
    groupKey: 'nav.groupManagement',
    adminOnly: true,
  },
  {
    id: 'exchange.profile',
    to: `${ZONE_PATHS.exchange}/profil`,
    labelKey: 'nav.profile',
    placement: 'topbar',
  },
]);

/**
 * Platform yonetimi. Buraya yalniz `super_admin` girer ve BASKA BOLGE GORMEZ
 * (bkz. zone-home.ts). Buro yetki kodlari burada gecersiz: bu ekranlar
 * organizasyon icinde degil, organizasyonlarin ustunde calisir.
 */
const adminMenu = freeze([
  {
    id: 'admin.users',
    to: ZONE_PATHS.admin,
    labelKey: 'nav.platformUsers',
    groupKey: 'nav.groupPlatform',
  },
  {
    id: 'admin.organizations',
    to: `${ZONE_PATHS.admin}/isletmeler`,
    labelKey: 'nav.platformOrganizations',
    groupKey: 'nav.groupPlatform',
  },
  {
    id: 'admin.metrics',
    to: `${ZONE_PATHS.admin}/metrikler`,
    labelKey: 'nav.platformMetrics',
    groupKey: 'nav.groupPlatform',
  },
  // Uyari cozum kuyrugu (2026-09-23, karar Q3): uyarili islemler 'resolved' / 'refunded' ile
  // yalniz buradan kapanir.
  {
    id: 'admin.warnedTasks',
    to: `${ZONE_PATHS.admin}/uyarilar`,
    labelKey: 'nav.platformWarnedTasks',
    groupKey: 'nav.groupPlatform',
  },
  {
    id: 'admin.auditLog',
    to: `${ZONE_PATHS.admin}/gecmis`,
    labelKey: 'nav.auditLog',
    groupKey: 'nav.groupPlatform',
  },
  {
    id: 'admin.profile',
    to: `${ZONE_PATHS.admin}/profil`,
    labelKey: 'nav.profile',
    placement: 'topbar',
  },
]);

export const ZONE_MENUS: Readonly<Record<Zone, readonly MenuItem[]>> = Object.freeze({
  customer: customerMenu,
  exchange: exchangeMenu,
  admin: adminMenu,
});

/** Bolgenin baslik anahtari — kabuktaki bolge etiketi. */
export const ZONE_TITLE_KEYS: Readonly<Record<Zone, string>> = Object.freeze({
  customer: 'nav.zoneCustomer',
  exchange: 'nav.zoneExchange',
  admin: 'nav.zoneAdmin',
});
